import type { Lang } from "../index";

export interface ZoomRealtimeTranslationCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: ZoomRealtimeTranslationCopy = {
  seo: {
    title: "Real-time Zoom translation — EN ↔ PT/ES live, secure",
    description:
      "Real-time translation for Zoom, Teams and Meet. Live English transcript next to your-language translation, processed securely with zero-retention.",
  },
  eyebrow: "Use case · Real-time translation",
  h1: "The Zoom is in English. Your brain isn't, yet.",
  lede:
    "Live EN ↔ PT/ES translation that runs next to the English transcript on your computer while the call is happening. Built for non-native English speakers who keep up in writing but lose ground at native speed.",
  problem: {
    heading: "English captions don't fix the problem",
    body1:
      "The Zoom meeting is in English. You speak English, sure — well enough for 1-on-1s, well enough for prepared talks. But this is sprint planning. Two engineers debating a refactor. Three different acronyms per minute. The technical jargon is fine when you read it, but spoken at native speed it slips past. By the time you mentally translate the previous sentence, you've missed the next two.",
    body2:
      "Zoom's built-in captions help — they show the English. But you don't need *English written down faster*; you need the meaning **in your language** so you can keep up at the pace the call is actually moving. tl;dv and Otter give you a translated transcript after the call. That's a post-mortem, not a copilot.",
  },
  scenarios: {
    heading: "Where it pays off",
    items: [
      {
        tag: "Sprint planning",
        title: "\"…and we'll punt the cache layer to the next iteration\"",
        body: "Two engineers debating in English. Minuta shows the live English transcript on one side and a live PT/ES translation on the other — synced, latency-bounded. You read the meaning in your language and respond in English at your normal pace.",
      },
      {
        tag: "Customer call",
        title: "\"What you're describing sounds like a SOX compliance issue\"",
        body: "Customer drops a domain term you've never heard. Live transcript catches it. PT/ES translation gives you the gist — and you can hit the copilot for a one-line explanation grounded in your sales docs. You handle the question without breaking the call.",
      },
      {
        tag: "All-hands",
        title: "\"…and that brings us to the Q3 roadmap reshuffle\"",
        body: "Exec talks fast for 8 minutes. Live translation runs side-by-side with the English transcript. Afterwards, structured summary in your language: TL;DR, key points, what's changed. You don't need to ping a colleague to confirm what the CEO actually said.",
      },
    ],
  },
  how: {
    heading: "How Minuta plugs into a Zoom call",
    body:
      "Open Minuta a minute before the call. Pick your translation language. Run the call as you always would — Minuta captures system audio in the background, so the platform doesn't matter.",
    bullets: [
      "Works on **any** call platform — Zoom (web or native), Teams, Meet, FaceTime, even a YouTube live or a Loom recording. Minuta captures system audio on macOS, no extension or plugin.",
      "Two synchronised panels — English transcript + your-language translation, both live.",
      "Secure capture, zero retention. Audio is captured locally and processed securely via high-speed cloud APIs with a strict zero-data-retention policy.",
      "After the call: bilingual summary with TL;DR + action items in your language, copy-paste into your followup notes.",
    ],
  },
  proof: {
    heading: "Why pick Minuta over Zoom captions or post-call tools",
    bullets: [
      "Zoom captions are English-only and you don't need English written faster — you need the meaning in your language at the speed the call is moving.",
      "Otter, tl;dv, Fireflies translate **after** the call. That's a recap, not a copilot. Different problem.",
      "High-speed translation uses secure transient cloud APIs with zero data training or retention, keeping the resulting files local to your computer.",
      "Works without a bot in the call, without an admin install, without a Chrome extension. Just open Minuta and start the call.",
    ],
  },
  cta: {
    heading: "Translate your next Zoom in real time",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ZoomRealtimeTranslationCopy = {
  seo: {
    title: "Traduzir reunião Zoom em tempo real — EN ↔ PT ao vivo e seguro",
    description:
      "Como traduzir reunião Zoom em tempo real para PT-BR. Transcript em inglês ao vivo + tradução simultânea segura com retenção zero. Pra quem fala inglês mas perde o pé em call rápida.",
  },
  eyebrow: "Caso de uso · Tradução em tempo real",
  h1: "A reunião do Zoom é em inglês. O seu cérebro ainda não.",
  lede:
    "Tradução EN ↔ PT em tempo real, rodando do lado do transcript em inglês no seu computador enquanto a call acontece. Feito pra quem fala inglês escrito mas perde o pé na velocidade nativa.",
  problem: {
    heading: "Legenda em inglês não resolve",
    body1:
      "A reunião do Zoom é em inglês. Você fala inglês, sim — bem o suficiente pra 1-on-1, bem o suficiente pra apresentação preparada. Mas isso é sprint planning. Dois engenheiros debatendo um refactor. Três siglas diferentes por minuto. O jargão técnico você entende quando lê, mas falado em velocidade nativa escapa. Quando você termina de traduzir mentalmente a frase anterior, já perdeu as duas seguintes.",
    body2:
      "A legenda nativa do Zoom ajuda — mostra o inglês. Mas você não precisa de *inglês escrito mais rápido*; precisa do sentido **na sua língua** pra acompanhar no ritmo em que a call está acontecendo. Por isso o que resolve é traduzir reunião Zoom em tempo real, não pegar transcript depois. tl;dv e Otter te dão um transcript traduzido depois da call. Isso é post-mortem, não copiloto.",
  },
  scenarios: {
    heading: "Onde paga o investimento",
    items: [
      {
        tag: "Sprint planning",
        title: "\"…and we'll punt the cache layer to the next iteration\"",
        body: "Dois engenheiros debatendo em inglês. O Minuta mostra o transcript em inglês ao vivo de um lado e a tradução PT em tempo real do outro — sincronizado, com latência controlada. Você lê o sentido em português e responde em inglês no seu ritmo.",
      },
      {
        tag: "Call de cliente",
        title: "\"What you're describing sounds like a SOX compliance issue\"",
        body: "Cliente solta um termo de domínio que você nunca ouviu. Transcript ao vivo captura. Tradução em PT te dá o sentido — e você ainda chama o copiloto pra uma explicação de uma linha fundamentada nos seus docs de vendas. Responde sem travar a call.",
      },
      {
        tag: "All-hands",
        title: "\"…and that brings us to the Q3 roadmap reshuffle\"",
        body: "Exec fala rápido por 8 minutos. A tradução ao vivo roda lado a lado com o transcript em inglês. Depois, resumo estruturado em português: TL;DR, pontos-chave, o que mudou. Você não precisa pingar um colega pra confirmar o que o CEO disse de verdade.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta entra numa call do Zoom",
    body:
      "Abre o Minuta um minuto antes da call. Escolhe a língua de tradução. Roda a call como sempre roda — o Minuta captura o áudio do sistema em segundo plano, então a plataforma não importa.",
    bullets: [
      "Funciona em **qualquer** plataforma de call — Zoom (web ou nativo), Teams, Meet, FaceTime, até live do YouTube ou gravação do Loom. O Minuta captura áudio do sistema no macOS, sem extensão nem plugin.",
      "Dois painéis sincronizados — transcript em inglês + tradução na sua língua, os dois ao vivo.",
      "Captura segura com retenção zero. O áudio é capturado no sistema e processado via APIs de nuvem rápidas com política rígida de retenção zero de dados.",
      "Depois da call: resumo bilíngue com TL;DR + action items em português, cola no seu followup.",
    ],
  },
  proof: {
    heading: "Por que escolher Minuta em vez da legenda do Zoom ou de ferramenta pós-call",
    bullets: [
      "Legenda do Zoom é só em inglês e você não precisa de inglês escrito mais rápido — precisa do sentido em português na velocidade em que a call está rolando.",
      "Otter, tl;dv, Fireflies traduzem **depois** da call. Isso é recap, não copiloto. Problema diferente.",
      "A tradução em tempo real usa APIs transientes seguras em nuvem com retenção zero, salvando todos os seus registros de forma local.",
      "Funciona sem bot na call, sem instalação de admin, sem extensão de Chrome. Abre o Minuta e começa a call.",
    ],
  },
  cta: {
    heading: "Traduz a sua próxima reunião do Zoom em tempo real",
    body: "7 dias com tudo desbloqueado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ZoomRealtimeTranslationCopy = {
  seo: {
    title: "Traducir reunión Zoom en tiempo real — EN ↔ ES en vivo y seguro",
    description:
      "Cómo traducir reunión Zoom en tiempo real al español. Transcript en inglés en vivo + traducción simultánea segura con retención cero. Para quien habla inglés pero pierde el ritmo en calls rápidas.",
  },
  eyebrow: "Caso de uso · Traducción en tiempo real",
  h1: "La reunión de Zoom es en inglés. Tu cerebro todavía no.",
  lede:
    "Traducción EN ↔ ES en tiempo real, corriendo al lado del transcript en inglés en tu computadora mientras la call está pasando. Hecho para quien habla inglés escrito pero pierde el ritmo a velocidad nativa.",
  problem: {
    heading: "Los subtítulos en inglés no resuelven el problema",
    body1:
      "La reunión de Zoom es en inglés. Hablas inglés, sí — lo suficiente para 1-on-1s, lo suficiente para una charla preparada. Pero esto es sprint planning. Dos ingenieros debatiendo un refactor. Tres siglas diferentes por minuto. La jerga técnica la entiendes cuando la lees, pero hablada a velocidad nativa se escapa. Para cuando terminas de traducir mentalmente la frase anterior, ya perdiste las dos siguientes.",
    body2:
      "Los subtítulos nativos de Zoom ayudan — muestran el inglés. Pero no necesitas *inglés escrito más rápido*; necesitas el sentido **en tu idioma** para seguir el ritmo al que la call está pasando. Por eso lo que resuelve es traducir reunión Zoom tiempo real, no recibir un transcript después. tl;dv y Otter te dan un transcript traducido después de la call. Eso es post-mortem, no copiloto.",
  },
  scenarios: {
    heading: "Dónde paga la inversión",
    items: [
      {
        tag: "Sprint planning",
        title: "\"…and we'll punt the cache layer to the next iteration\"",
        body: "Dos ingenieros debatiendo en inglés. Minuta muestra el transcript en inglés en vivo de un lado y la traducción al ES en tiempo real del otro — sincronizado, con latencia acotada. Lees el sentido en español y respondes en inglés a tu ritmo.",
      },
      {
        tag: "Call con cliente",
        title: "\"What you're describing sounds like a SOX compliance issue\"",
        body: "El cliente suelta un término de dominio que nunca oíste. El transcript en vivo lo captura. La traducción al ES te da el sentido — y todavía llamas al copiloto para una explicación de una línea basada en tus docs de ventas. Manejas la pregunta sin trabar la call.",
      },
      {
        tag: "All-hands",
        title: "\"…and that brings us to the Q3 roadmap reshuffle\"",
        body: "El exec habla rápido por 8 minutos. La traducción en vivo corre lado a lado con el transcript en inglés. Después, resumen estructurado en español: TL;DR, puntos clave, qué cambió. No necesitas pingear a un colega para confirmar lo que el CEO realmente dijo.",
      },
    ],
  },
  how: {
    heading: "Cómo entra Minuta en una call de Zoom",
    body:
      "Abres Minuta un minuto antes de la call. Eliges el idioma de traducción. Corres la call como siempre — Minuta captura el audio del sistema en segundo plano, así que la plataforma da igual.",
    bullets: [
      "Funciona en **cualquier** plataforma de call — Zoom (web o nativo), Teams, Meet, FaceTime, hasta un live de YouTube o una grabación de Loom. Minuta captura audio del sistema en macOS, sin extensión ni plugin.",
      "Dos paneles sincronizados — transcript en inglés + traducción en tu idioma, ambos en vivo.",
      "Captura segura con retención cero. El audio se captura de forma local y se procesa mediante APIs veloces en la nube con política estricta de retención cero.",
      "Después de la call: resumen bilingüe con TL;DR + action items en español, lo pegas en tu followup.",
    ],
  },
  proof: {
    heading: "Por qué elegir Minuta en vez de los subtítulos de Zoom o de una herramienta post-call",
    bullets: [
      "Los subtítulos de Zoom son solo en inglés y no necesitas inglés escrito más rápido — necesitas el sentido en español a la velocidad a la que la call está pasando.",
      "Otter, tl;dv, Fireflies traducen **después** de la call. Eso es recap, no copiloto. Problema distinto.",
      "La traducción en vivo corre de forma veloz a través de APIs transitorias seguras con retención cero, almacenando los archivos localmente.",
      "Funciona sin bot en la call, sin instalación de admin, sin extensión de Chrome. Abres Minuta y arrancas la call.",
    ],
  },
  cta: {
    heading: "Traduce tu próxima reunión de Zoom en tiempo real",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const zoomRealtimeTranslationCopy: Record<Lang, ZoomRealtimeTranslationCopy> = { en, pt, es };
